export const DEFAULT_NORMAL_SELL_ECONOMICS = Object.freeze({
  baseMinProfitBps: 30,
  exitFeeBps: 5,
  expectedSlippageBps: 3,
  expectedPriceImpactBps: 1,
  requiredNetProfitBps: 10,
  fallbackGasUsd: 0.1,
});

export const DEFAULT_MIN_BUY_SPACING_MS = 60000;

export const DEFAULT_COMPOUND_TRADE_SIZING = Object.freeze({
  baseTradePct: 0.20,
  maxTradePct: 0.25,
  economicMinTradeUsd: 50,
  absoluteMaxTradeUsd: 500,
});

const finiteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

export function validateConfirmedTradeFill({
  eventType,
  status,
  side,
  marketKey,
  txHash,
  amountIn,
  amountOut,
  hasReceiptEvidence,
  processedTxHashes = [],
} = {}) {
  const normalizedSide = String(side || "").toUpperCase();
  const normalizedStatus = String(status || "").toUpperCase();
  const normalizedType = String(eventType || "").toUpperCase();
  const normalizedHash = String(txHash || "").toLowerCase();

  if (normalizedType !== "TRADE_FILL") return { allowed: false, reason: "not a trade fill" };
  if (normalizedStatus !== "CONFIRMED") return { allowed: false, reason: "trade is not confirmed" };
  if (!['BUY', 'SELL'].includes(normalizedSide)) return { allowed: false, reason: "invalid trade side" };
  if (!String(marketKey || "").trim()) return { allowed: false, reason: "missing market key" };
  if (!/^0x[0-9a-f]{64}$/.test(normalizedHash)) return { allowed: false, reason: "missing confirmed txHash" };
  if (hasReceiptEvidence !== true) return { allowed: false, reason: "missing receipt transfer evidence" };
  if (!(finiteNumber(amountIn) > 0) || !(finiteNumber(amountOut) > 0)) {
    return { allowed: false, reason: "invalid confirmed amounts" };
  }
  if ((processedTxHashes || []).map((hash) => String(hash).toLowerCase()).includes(normalizedHash)) {
    return { allowed: false, reason: "duplicate txHash" };
  }
  return { allowed: true, reason: "confirmed receipt-backed trade fill" };
}

export function detectExternalBalanceFlows({
  previousStableBalance,
  previousAssetBalance,
  currentStableBalance,
  currentAssetBalance,
  expectedStableDelta = 0,
  expectedAssetDelta = 0,
  assetPrice,
  stableTolerance = 0.01,
  assetTolerance = 0.00001,
} = {}) {
  const previousStable = finiteNumber(previousStableBalance);
  const previousAsset = finiteNumber(previousAssetBalance);
  const currentStable = Math.max(0, finiteNumber(currentStableBalance));
  const currentAsset = Math.max(0, finiteNumber(currentAssetBalance));
  const stableResidual = currentStable - previousStable - finiteNumber(expectedStableDelta);
  const assetResidual = currentAsset - previousAsset - finiteNumber(expectedAssetDelta);
  const price = Math.max(0, finiteNumber(assetPrice));
  const flows = [];

  if (Math.abs(stableResidual) > Math.max(0, finiteNumber(stableTolerance))) {
    flows.push({
      type: stableResidual > 0 ? "USDC_DEPOSIT" : "USDC_WITHDRAWAL",
      token: "USDC",
      amount: Math.abs(stableResidual),
      usdValue: Math.abs(stableResidual),
      signedTokenDelta: stableResidual,
    });
  }
  if (price > 0 && Math.abs(assetResidual) > Math.max(0, finiteNumber(assetTolerance))) {
    flows.push({
      type: assetResidual > 0 ? "WETH_DEPOSIT" : "WETH_WITHDRAWAL",
      token: "WETH",
      amount: Math.abs(assetResidual),
      usdValue: Math.abs(assetResidual) * price,
      signedTokenDelta: assetResidual,
    });
  }

  return { stableResidual, assetResidual, flows };
}

export function applyExternalAssetFlowToLedger({
  holdings,
  costBasis,
  signedAssetDelta,
  assetPrice,
} = {}) {
  const currentHoldings = Math.max(0, finiteNumber(holdings));
  const currentCost = Math.max(0, finiteNumber(costBasis));
  const delta = finiteNumber(signedAssetDelta);
  const price = Math.max(0, finiteNumber(assetPrice));

  if (delta > 0) {
    return {
      holdings: currentHoldings + delta,
      costBasis: currentCost + delta * price,
      costBasisTransferred: delta * price,
    };
  }
  if (delta < 0 && currentHoldings > 0) {
    const removed = Math.min(currentHoldings, Math.abs(delta));
    const removedFraction = removed / currentHoldings;
    const costBasisTransferred = currentCost * removedFraction;
    return {
      holdings: Math.max(0, currentHoldings - removed),
      costBasis: Math.max(0, currentCost - costBasisTransferred),
      costBasisTransferred,
    };
  }
  return { holdings: currentHoldings, costBasis: currentCost, costBasisTransferred: 0 };
}

export function calculateFlowAdjustedPnl({
  currentStableBalance,
  currentAssetBalance,
  assetPrice,
  baselineEquityUsd,
  netDepositsUsd = 0,
  netWithdrawalsUsd = 0,
  totalGasUsd = 0,
} = {}) {
  const currentEquityUsd =
    Math.max(0, finiteNumber(currentStableBalance)) +
    Math.max(0, finiteNumber(currentAssetBalance)) * Math.max(0, finiteNumber(assetPrice));
  return {
    currentEquityUsd,
    pnlUsd:
      currentEquityUsd -
      Math.max(0, finiteNumber(baselineEquityUsd)) -
      Math.max(0, finiteNumber(netDepositsUsd)) +
      Math.max(0, finiteNumber(netWithdrawalsUsd)) -
      Math.max(0, finiteNumber(totalGasUsd)),
  };
}

export function calculateRealizedCompoundingEquity({
  currentEquityUsd,
  baselineEquityUsd,
  netDepositsUsd = 0,
  netWithdrawalsUsd = 0,
  realizedPnlUsd = 0,
  realizedPnlOffsetUsd = 0,
} = {}) {
  const current = Math.max(0, finiteNumber(currentEquityUsd));
  const flowAdjustedBaseline = Math.max(
    0,
    Math.max(0, finiteNumber(baselineEquityUsd)) +
      Math.max(0, finiteNumber(netDepositsUsd)) -
      Math.max(0, finiteNumber(netWithdrawalsUsd))
  );
  const realizedSinceCheckpoint =
    finiteNumber(realizedPnlUsd) - finiteNumber(realizedPnlOffsetUsd);
  const realizedCapital = Math.max(0, flowAdjustedBaseline + realizedSinceCheckpoint);
  return {
    sizingEquityUsd: Math.min(current, realizedCapital),
    flowAdjustedBaselineUsd: flowAdjustedBaseline,
    realizedSinceCheckpointUsd: realizedSinceCheckpoint,
  };
}

export function calculateCompoundedTradeSize({
  sizingEquityUsd,
  availableUsd,
  baseTradePct = DEFAULT_COMPOUND_TRADE_SIZING.baseTradePct,
  maxTradePct = DEFAULT_COMPOUND_TRADE_SIZING.maxTradePct,
  economicMinTradeUsd = DEFAULT_COMPOUND_TRADE_SIZING.economicMinTradeUsd,
  absoluteMaxTradeUsd = DEFAULT_COMPOUND_TRADE_SIZING.absoluteMaxTradeUsd,
  multiplier = 1,
} = {}) {
  const equity = Math.max(0, finiteNumber(sizingEquityUsd));
  const available = Math.max(0, finiteNumber(availableUsd));
  const basePct = Math.min(1, Math.max(0, finiteNumber(baseTradePct)));
  const maxPct = Math.min(1, Math.max(basePct, finiteNumber(maxTradePct)));
  const economicMin = Math.max(0, finiteNumber(economicMinTradeUsd));
  const absoluteMax = Math.max(0, finiteNumber(absoluteMaxTradeUsd));
  const appliedMultiplier = Math.min(1.2, Math.max(0.8, finiteNumber(multiplier) || 1));
  const hardMaxUsd = Math.min(available, absoluteMax, equity * maxPct);
  const rawBaseTradeUsd = equity * basePct;
  const minimumRelaxed = economicMin > hardMaxUsd + 1e-9;
  const effectiveMinTradeUsd = minimumRelaxed ? 0 : economicMin;

  if (!(hardMaxUsd > 0)) {
    return {
      allowed: false,
      reason: "no spendable balance under percentage cap",
      sizingEquityUsd: equity,
      rawBaseTradeUsd,
      baseTradeUsd: 0,
      finalTradeUsd: 0,
      economicMinTradeUsd: economicMin,
      effectiveMinTradeUsd,
      minimumRelaxed,
      hardMaxUsd,
      appliedMultiplier,
    };
  }

  const baseTradeUsd = Math.min(hardMaxUsd, Math.max(effectiveMinTradeUsd, rawBaseTradeUsd));
  const finalTradeUsd = Math.min(hardMaxUsd, Math.max(effectiveMinTradeUsd, baseTradeUsd * appliedMultiplier));
  return {
    allowed: true,
    reason: minimumRelaxed
      ? "economic minimum relaxed to respect percentage cap"
      : "flow-adjusted compound sizing",
    sizingEquityUsd: equity,
    rawBaseTradeUsd,
    baseTradeUsd,
    finalTradeUsd,
    economicMinTradeUsd: economicMin,
    effectiveMinTradeUsd,
    minimumRelaxed,
    hardMaxUsd,
    appliedMultiplier,
  };
}

export function getAuthoritativeAverageEntry({
  costBasis,
  ledgerHoldings,
  positionAvgEntry = 0,
} = {}) {
  const confirmedCost = Math.max(0, Number(costBasis) || 0);
  const confirmedHoldings = Math.max(0, Number(ledgerHoldings) || 0);
  if (confirmedCost > 0 && confirmedHoldings > 1e-10) {
    return {
      avgEntry: confirmedCost / confirmedHoldings,
      source: "confirmed-fill-ledger",
    };
  }
  return {
    avgEntry: Math.max(0, Number(positionAvgEntry) || 0),
    source: "position-fallback",
  };
}

export function getSafeTimeReanchorTargets({
  currentPrice,
  avgEntry,
  buyMultiplier,
  sellMultiplier,
  minimumSellPrice = 0,
} = {}) {
  const price = Math.max(0, Number(currentPrice) || 0);
  const entry = Math.max(0, Number(avgEntry) || 0);
  const buy = Math.max(0, Number(buyMultiplier) || 0);
  const sell = Math.max(0, Number(sellMultiplier) || 0);
  const sellFloor = Math.max(0, Number(minimumSellPrice) || 0);
  if (!(price > 0) || !(entry > 0) || !(buy > 0) || !(sell > 0)) {
    return { priceTargetBuy: 0, priceTargetSell: 0 };
  }
  return {
    // An idle reanchor may trail a stale target down, but must never make an
    // already-underwater BUY immediately executable above the current spot.
    priceTargetBuy: Math.min(price * buy, entry * buy),
    priceTargetSell: Math.max(price * sell, entry * sell, sellFloor),
  };
}

export function evaluateProfitReanchor({
  tradePnl,
  realizedCostRemoved,
  receivedUsd,
  minTradeUsd,
  requiredReturnPct = 0.025,
} = {}) {
  const pnl = Number(tradePnl) || 0;
  const removedCost = Math.max(0, Number(realizedCostRemoved) || 0);
  const proceeds = Math.max(0, Number(receivedUsd) || 0);
  const configuredMinimum = Math.max(0, Number(minTradeUsd) || 0);
  const minimumNotionalUsd = Math.max(25, configuredMinimum * 0.5);
  const tradeReturnPct = removedCost > 0 ? pnl / removedCost : 0;
  const allowed =
    pnl > 0 &&
    removedCost > 0 &&
    proceeds >= minimumNotionalUsd &&
    tradeReturnPct >= Math.max(0, Number(requiredReturnPct) || 0);

  return {
    allowed,
    tradeReturnPct,
    minimumNotionalUsd,
    reason: allowed
      ? "meaningful realized profit"
      : proceeds < minimumNotionalUsd
        ? "sell notional is dust/too small"
        : tradeReturnPct < requiredReturnPct
          ? "trade return below reanchor threshold"
          : "trade did not realize profit",
  };
}

export function getCostAwareNormalSellFloor({
  avgEntry,
  tradeUsd,
  estimatedGasUsd,
  baseMinProfitBps = DEFAULT_NORMAL_SELL_ECONOMICS.baseMinProfitBps,
  exitFeeBps = DEFAULT_NORMAL_SELL_ECONOMICS.exitFeeBps,
  expectedSlippageBps = DEFAULT_NORMAL_SELL_ECONOMICS.expectedSlippageBps,
  expectedPriceImpactBps = DEFAULT_NORMAL_SELL_ECONOMICS.expectedPriceImpactBps,
  requiredNetProfitBps = DEFAULT_NORMAL_SELL_ECONOMICS.requiredNetProfitBps,
  fallbackGasUsd = DEFAULT_NORMAL_SELL_ECONOMICS.fallbackGasUsd,
} = {}) {
  const entry = Math.max(0, Number(avgEntry) || 0);
  const sizeUsd = Math.max(0, Number(tradeUsd) || 0);
  const gasUsd = Math.max(
    0,
    Number.isFinite(Number(estimatedGasUsd))
      ? Number(estimatedGasUsd)
      : Number(fallbackGasUsd) || 0
  );
  const gasBps = sizeUsd > 0 ? (gasUsd / sizeUsd) * 10000 : 0;

  // avgEntry is based on confirmed spend/receive amounts, so entry execution
  // costs are already in the cost basis. Only exit costs belong in this floor.
  const exitCostBps =
    Math.max(0, Number(exitFeeBps) || 0) +
    Math.max(0, Number(expectedSlippageBps) || 0) +
    Math.max(0, Number(expectedPriceImpactBps) || 0) +
    gasBps;
  const costAwareBps = exitCostBps + Math.max(0, Number(requiredNetProfitBps) || 0);
  const requiredProfitBps = Math.max(
    Math.max(0, Number(baseMinProfitBps) || 0),
    costAwareBps
  );

  return {
    floorPrice: entry > 0 ? entry * (1 + requiredProfitBps / 10000) : 0,
    requiredProfitBps,
    exitCostBps,
    gasBps,
    gasUsd,
    tradeUsd: sizeUsd,
  };
}

export function evaluateNormalSellDuplicateGuard({
  now = Date.now(),
  lastSellAt,
  lastSellPrice,
  currentPrice,
  cooldownMs = 90000,
  minAdvanceBps = 12,
  bypass = false,
} = {}) {
  const previousAt = Math.max(0, Number(lastSellAt) || 0);
  const previousPrice = Math.max(0, Number(lastSellPrice) || 0);
  const price = Math.max(0, Number(currentPrice) || 0);
  const cooldown = Math.max(0, Number(cooldownMs) || 0);
  const requiredAdvanceBps = Math.max(0, Number(minAdvanceBps) || 0);

  if (bypass || !previousAt || !(previousPrice > 0) || !(price > 0)) {
    return {
      allowed: true,
      bypassed: bypass,
      elapsedMs: previousAt ? Math.max(0, Number(now) - previousAt) : Infinity,
      cooldownRemainingMs: 0,
      priceAdvanceBps: previousPrice > 0 && price > 0
        ? (price / previousPrice - 1) * 10000
        : Infinity,
      reason: bypass ? "risk-control/final-exit bypass" : "no prior normal sell",
    };
  }

  const elapsedMs = Math.max(0, Number(now) - previousAt);
  const priceAdvanceBps = (price / previousPrice - 1) * 10000;
  const cooldownElapsed = elapsedMs >= cooldown;
  const priceAdvanced = priceAdvanceBps >= requiredAdvanceBps;
  const allowed = cooldownElapsed || priceAdvanced;

  return {
    allowed,
    bypassed: false,
    elapsedMs,
    cooldownRemainingMs: Math.max(0, cooldown - elapsedMs),
    priceAdvanceBps,
    reason: allowed
      ? priceAdvanced
        ? "price advanced"
        : "cooldown elapsed"
      : "same-level normal sell inside cooldown",
  };
}

export function computeStuckStopCounterfactual({
  soldWeth,
  receivedUsdc,
  reboughtWeth = 0,
  rebuyUsdc = 0,
  currentPrice,
} = {}) {
  const sold = Math.max(0, Number(soldWeth) || 0);
  const received = Math.max(0, Number(receivedUsdc) || 0);
  const rebought = Math.min(sold, Math.max(0, Number(reboughtWeth) || 0));
  const rebuyCost = Math.max(0, Number(rebuyUsdc) || 0);
  const price = Math.max(0, Number(currentPrice) || 0);
  const netWethReduced = Math.max(0, sold - rebought);
  const netUsdcAdded = received - rebuyCost;
  const holdValueUsd = netWethReduced * price;

  return {
    netWethReduced,
    netUsdcAdded,
    holdValueUsd,
    advantageVsHoldUsd: netUsdcAdded - holdValueUsd,
    breakEvenWethPrice:
      netWethReduced > 0 && netUsdcAdded > 0
        ? netUsdcAdded / netWethReduced
        : null,
  };
}
